import * as React from "react";
import { humanizer as humanizerFactory, HumanizerOptions } from "humanize-duration";
import { IS_DEPLOYMENT_ENV_TEST } from "../envVariables";
import { Tooltip as AntTooltip, Button, notification } from "antd";
import clsx from "clsx";
import { useAppSelector } from "../store/hooks";
import { selectValue } from "../store/timezoneSlice";
import { formatDate, DateFormatType, isHumanizeFormat } from "../utils/dateFormat";

// =============================================================================
// Humanize Duration
// =============================================================================

const humanizeUnits = ["y", "mo", "w", "d", "h", "m"];
const conversionRates: Record<string, number> = {
  ms: 1,
  s: 1000,
  m: 1000 * 60,
  h: 1000 * 60 * 60,
  d: 1000 * 60 * 60 * 24,
  w: 1000 * 60 * 60 * 24 * 7,
  mo: 1000 * 60 * 60 * 24 * 30,
  y: 1000 * 60 * 60 * 24 * 365,
};

export function humanize(ms: number, ago?: boolean, options?: HumanizerOptions) {
  ms = new Date().getTime() - ms;
  const finalOptions: HumanizerOptions = {
    round: false,
    units: humanizeUnits as any,
    largest: 1,
    ...(options || {}),
  };

  const flooredDuration: number = floorDuration(ms, finalOptions.units || humanizeUnits);
  const result = humanizerFactory(finalOptions)(flooredDuration, finalOptions);

  if (result === "0 minutes") {
    return "Now";
  }

  if (ago) {
    return result + " ago";
  }
  return result;
}

function floorDuration(milliseconds: number, units: any[]): number {
  const smallestUnit: string = units[units.length - 1];
  const conversionRate: number = conversionRates[smallestUnit];
  return Math.floor(milliseconds / conversionRate) * conversionRate;
}

// =============================================================================
// DateString Component
// =============================================================================

interface Props {
  iso: string;
  prefix?: string;
  format?: string;
  utc?: boolean;
  invalidText?: string;
  style?: React.CSSProperties;
  className?: string;
  /** Pre-computed formatted value - skips formatting calculation when provided */
  precomputedValue?: string;
}

export const DateString: React.FC<Props> = ({
  iso,
  prefix,
  format,
  utc = false,
  invalidText = "",
  style = {},
  className = "",
  precomputedValue,
}) => {
  const timezoneValue = useAppSelector(selectValue);

  // Debug mode check
  function getDebug() {
    return (localStorage.getItem("CPLN_DEBUG") || "") === "true";
  }
  const [_debug] = React.useState(getDebug());
  const debug = IS_DEPLOYMENT_ENV_TEST || _debug;

  // Early return for empty iso
  if (!iso) {
    return null;
  }

  // Parse date
  const date = new Date(iso);
  if (isNaN(date.getTime())) {
    return <span>{invalidText}</span>;
  }

  // Determine format type
  const formatType = (format || "sec") as DateFormatType;
  const isHumanize = isHumanizeFormat(format || "");

  // Use precomputed value if provided, otherwise compute
  const formattedValue = precomputedValue ?? (
    isHumanize
      ? humanize(date.getTime(), format?.includes("Ago"))
      : formatDate(iso, formatType, { timezone: utc ? "UTC" : timezoneValue, utc })
  );

  const formattedUTC = formatDate(iso, isHumanize ? "sec" : formatType, { utc: true });
  const formattedLocal = formatDate(iso, isHumanize ? "sec" : formatType, { timezone: timezoneValue });

  // Debug info for tooltip
  const debugInfo = [
    { label: "Timezone", value: timezoneValue },
    { label: "ISO", value: iso },
    { label: "Local time", value: formattedLocal },
    { label: "UTC time", value: formattedUTC },
    { label: "Humanized", value: humanize(date.getTime(), false), skip: !isHumanize },
  ];

  return (
    <AntTooltip
      mouseEnterDelay={1}
      trigger={["click", "hover"]}
      title={
        <div className="text-xs flex flex-col gap-1">
          {debugInfo.map((info, index) => {
            if (info.skip) {
              return null;
            }
            return (
              <div key={index}>
                <div>{info.label}</div>
                <div>{info.value}</div>
              </div>
            );
          })}
          <Button
            type="default"
            size={"small"}
            onClick={() => {
              navigator.clipboard.writeText(JSON.stringify(debugInfo));
              notification.info({ message: "Copied to clipboard" });
            }}
          >
            Copy
          </Button>
        </div>
      }
      open={debug === false ? false : undefined}
    >
      <span className={clsx(className)} style={style}>
        {prefix ? `${prefix} ` : ""}
        {formattedValue}
      </span>
    </AntTooltip>
  );
};
